import React, { useEffect, useMemo, useState } from "react";

import {
  Briefcase,
  ChevronRight,
  FileText,
  Layers,
  Users,
  Building2,
  Target,
  Shield,
  Home,
  TrendingUp,
  UserPlus,
  Activity,
  Mail,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

import HomeVideosSection from "../../Components/HomeVideosSection";
import PageHeader from "../../Components/PageHeader";
import TabsComponent from "../../Components/Tab";
import WelcomeBanner from "../../Components/WelcomeBanner";
import VendorLevelContainer from "../../Container/VendorAdminContainer/VendorLevelContainer";
import VendorProductTelemetryContainer from "../../Container/VendorAdminContainer/VendorProductTelemetryContainer";
import VendorTemplatesContainer from "../../Container/VendorAdminContainer/VendorTemplatesContainer";
import VendorUserManagementContainer from "../../Container/VendorAdminContainer/VendorUserManagementContainer";
import { useAppSelector, useAppDispatch } from "../../Store/Hooks";
import { fetchDashboardCounts } from "../../Store/Reducers/MasterSlice";
import { selectProductTelemetricsAccess } from "../../Store/Reducers/VendorAdminSlice";
import { fetchUserVideos } from "../../Store/Reducers/VideoSlice";
import {
  ROLE_DISPLAY_NAMES,
  normalizeRoleName,
} from "../../Utils/Constants/RoleConstants";
import {
  STORAGE_KEYS,
  getFromLocalStorage,
} from "../../Utils/Helper/LocalStorage";

import "./style.scss";

const VendorAdminHome: React.FC = () => {
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug?: string }>();
  const dispatch = useAppDispatch();

  const withOrg = (path: string) => {
    const p = path.startsWith("/") ? path : `/${path}`;
    return orgSlug ? `/${orgSlug}${p}` : p;
  };
  const { user } = useAppSelector(state => state.authentication);
  const { dashboardCounts } = useAppSelector(state => state.master);
  const { userVideos, userVideosLoading } = useAppSelector(
    (state: any) => state.video
  );
  const { resolved: telemetricsResolved, enabled: productTelemetricsEnabled } =
    useAppSelector(selectProductTelemetricsAccess);
  const loggedInUser = getFromLocalStorage(STORAGE_KEYS.LOGGED_IN_USER);

  const userName = loggedInUser?.fullName || user?.user?.full_name || "User";
  const organization = loggedInUser?.tenantName || user?.user?.tenant_domain;
  const userRole = user?.user?.role || loggedInUser?.role;
  const normalizedRole = normalizeRoleName(userRole);
  const roleDisplayName =
    normalizedRole && normalizedRole in ROLE_DISPLAY_NAMES
      ? ROLE_DISPLAY_NAMES[normalizedRole as keyof typeof ROLE_DISPLAY_NAMES]
      : "Vendor Admin";

  // Organization Overview metrics
  const organizationMetrics = [
    {
      title: "Executive Sponsors",
      value: dashboardCounts.data?.total_executive_sponsors ?? 0,
      icon: Users,
      colorClass: "text-blue-600",
      bgColorClass: "bg-blue-50",
      borderColor: "#3b82f6",
    },
    {
      title: "Leaders & C-Level",
      value: dashboardCounts.data?.total_leaders_c_level ?? 0,
      icon: Users,
      colorClass: "text-purple-600",
      bgColorClass: "bg-purple-50",
      borderColor: "#9333ea",
    },
    {
      title: "Org Admins",
      value: dashboardCounts.data?.total_org_admins ?? 0,
      icon: Shield,
      colorClass: "text-blue-700",
      bgColorClass: "bg-blue-50",
      borderColor: "#1e40af",
    },
    {
      title: "Total Accounts",
      value: dashboardCounts.data?.total_accounts ?? 0,
      icon: Building2,
      colorClass: "text-green-600",
      bgColorClass: "bg-green-50",
      borderColor: "#16a34a",
    },
    {
      title: "Active Assignments",
      value: dashboardCounts.data?.total_active_assignments ?? 0,
      icon: Target,
      colorClass: "text-orange-600",
      bgColorClass: "bg-orange-50",
      borderColor: "#ea580c",
    },
  ];

  const filterTabs = useMemo(() => {
    const tabs = [
      {
        id: "user-management",
        label: "User Management",
        icon: <Users size={18} />,
      },
      {
        id: "templates",
        label: "Templates",
        icon: <FileText size={18} />,
      },
      {
        id: "levels",
        label: "Levels",
        icon: <Layers size={18} />,
      },
    ];
    if (telemetricsResolved && productTelemetricsEnabled) {
      tabs.push({
        id: "product-telemetric-data",
        label: "Telemetry Data",
        icon: <Activity size={18} />,
      });
    }
    return tabs;
  }, [telemetricsResolved, productTelemetricsEnabled]);

  const [activeAdminFilter, setActiveAdminFilter] = useState("user-management");
  const handleAdminFilterChange = (tabId: string) =>
    setActiveAdminFilter(tabId);

  useEffect(() => {
    dispatch(fetchDashboardCounts());
    const roleToFetch = normalizedRole || userRole || "vendor_admin";
    dispatch(fetchUserVideos({ role: roleToFetch }) as any);
  }, [dispatch, normalizedRole, userRole]);

  useEffect(() => {
    if (
      activeAdminFilter === "product-telemetric-data" &&
      (!telemetricsResolved || !productTelemetricsEnabled)
    ) {
      setActiveAdminFilter("user-management");
    }
  }, [activeAdminFilter, telemetricsResolved, productTelemetricsEnabled]);

  const videos = useMemo(
    () => (userVideos && userVideos.length > 0 ? userVideos : []),
    [userVideos]
  );
  const hasVideos = videos.length > 0;

  return (
    <div className="bg-white overflow-hidden mt-3">
      <PageHeader title="Home" icon={Home} />
      <div className="w-full bg-white p-3 md:p-4">
        <div className="rounded-md bg-gray-100 p-4 md:p-4">
          <div className="vendor-admin-home">
            <WelcomeBanner
              userName={userName}
              roleDisplayName={roleDisplayName}
              organization={organization}
            />

            {/* Organization Overview + Videos – side by side (same as Executive Sponsors / C-Level Home) */}
            <div
              className={`grid grid-cols-1 ${
                hasVideos ? "lg:grid-cols-2" : "lg:grid-cols-1"
              } gap-6 mb-6`}
            >
              <div className="vendor-admin-home__section bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                <div className="mb-4">
                  <h2 className="text-xl font-semibold text-gray-900 mb-1 tracking-tight">
                    Organization Overview
                  </h2>
                  <p className="text-sm text-gray-500">
                    Key metrics and insights at a glance
                  </p>
                </div>
                <div
                  className={`grid ${hasVideos ? "grid-cols-2 gap-3 sm:gap-4" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"}`}
                >
                  {organizationMetrics.map((metric, index) => {
                    const IconComponent = metric.icon;
                    return (
                      <div
                        key={index}
                        className={`${metric.bgColorClass} rounded-xl p-4 flex items-center gap-3 min-h-[72px] sm:min-h-[80px] transition-shadow hover:shadow-sm`}
                      >
                        <div className={`${metric.colorClass} shrink-0`}>
                          <IconComponent
                            className="w-5 h-5 sm:w-6 sm:h-6"
                            size={24}
                          />
                        </div>
                        <div className="min-w-0">
                          <div
                            className={`text-xs sm:text-sm font-medium ${metric.colorClass}`}
                          >
                            {metric.title}
                          </div>
                          <div
                            className={`text-xl sm:text-2xl font-semibold tabular-nums ${metric.colorClass}`}
                          >
                            {metric.value}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Videos Section – right */}
              {hasVideos && (
                <HomeVideosSection
                  videos={videos}
                  loading={userVideosLoading}
                  sectionClassName="vendor-admin-home__section bg-white rounded-lg p-6 border border-gray-200 h-fit"
                  videoWrapClassName="vendor-admin-home__video-wrap relative w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden bg-gray-100 mb-3"
                  placeholderClassName="vendor-admin-home__video-placeholder w-full h-[240px] sm:h-[280px] rounded-2xl overflow-hidden border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-3 text-gray-500"
                  showDescription={true}
                />
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div
                onClick={() => navigate(withOrg("/always-on-channel"))}
                className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-purple-300/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500/50 focus-visible:outline-offset-2"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 rounded-lg p-3 group-hover:bg-purple-200/80 transition-colors">
                    <TrendingUp className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      Always On Intelligence
                    </h3>
                    <p className="text-sm text-gray-500">
                      View real-time insights
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-purple-600 transition-colors" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/ops"))}
                className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-purple-300/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500/50 focus-visible:outline-offset-2"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 rounded-lg p-3 group-hover:bg-blue-200/80 transition-colors">
                    <Briefcase className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      Executive Operations
                    </h3>
                    <p className="text-sm text-gray-500">
                      Manage accounts and operations
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-blue-600 transition-colors" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/easy-onboarding"))}
                className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-purple-300/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-purple-500/50 focus-visible:outline-offset-2"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 rounded-lg p-3 group-hover:bg-green-200/80 transition-colors">
                    <UserPlus className="w-6 h-6 text-green-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                      Easy Onboarding
                    </h3>
                    <p className="text-sm text-gray-500">
                      Send invitations to users
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-green-600 transition-colors" />
                </div>
              </div>

              <div
                onClick={() => navigate(withOrg("/email-activity"))}
                className="group bg-white border border-gray-200 rounded-lg p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-orange-300/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-orange-500/50 focus-visible:outline-offset-2"
              >
                <div className="flex items-center gap-4">
                  <div className="bg-orange-100 rounded-lg p-3 group-hover:bg-orange-200/80 transition-colors">
                    <Mail className="w-6 h-6 text-orange-600" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-0.5 whitespace-nowrap">
                      Email Activity
                    </h3>
                    <p className="text-sm text-gray-500">
                      View sent emails and history
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 shrink-0 ml-auto group-hover:text-orange-600 transition-colors" />
                </div>
              </div>
            </div>

            <div className="mb-4 bg-white rounded-lg p-4 border border-gray-200">
              <TabsComponent
                tabs={filterTabs}
                activeTab={activeAdminFilter}
                onTabChange={handleAdminFilterChange}
                variant="tab-segmented"
              />
            </div>

            <div className="bg-white rounded-lg p-6 md:p-6 mb-6 border border-gray-200 shadow-sm">
              <div>
                {activeAdminFilter === "user-management" && (
                  <VendorUserManagementContainer />
                )}
                {activeAdminFilter === "templates" && (
                  <VendorTemplatesContainer />
                )}
                {activeAdminFilter === "levels" && <VendorLevelContainer />}
                {activeAdminFilter === "product-telemetric-data" &&
                  productTelemetricsEnabled && (
                    <VendorProductTelemetryContainer />
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorAdminHome;
